import React from 'react';
import useThemeCanvas, { hexToRgb } from '../useThemeCanvas';

const STAR_COUNT = 120;

export default function AuroraTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: (ctx, w, h, state) => {
      state.ribbons = Array.from({ length: 5 }, (_, i) => ({
        phase: Math.random() * Math.PI * 2,
        speedY: 0.15 + Math.random() * 0.2,
        speedX: 0.05 + Math.random() * 0.1,
        amplitude: 60 + Math.random() * 80,
        yBase: h * (0.2 + 0.15 * i),
        colorIdx: i,
        shimmerPhase: Math.random() * Math.PI * 2,
      }));
      state.stars = Array.from({ length: STAR_COUNT }, () => ({
        x: Math.random() * w,
        y: Math.random() * h * 0.8,
        r: 0.4 + Math.random() * 1.3,
        phase: Math.random() * Math.PI * 2,
        freq: 0.8 + Math.random() * 1.6,
      }));
    },
    draw: (ctx, w, h, state, t) => {
      // Night-sky backdrop so the lights have something to glow against.
      const sky = ctx.createLinearGradient(0, 0, 0, h);
      if (dark) {
        sky.addColorStop(0, '#01030a');
        sky.addColorStop(0.6, '#04101f');
        sky.addColorStop(1, '#071a24');
      } else {
        sky.addColorStop(0, '#dbe7f5');
        sky.addColorStop(1, '#e9eef6');
      }
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, w, h);

      // Twinkling stars with a soft glow.
      const starBase = dark ? 0.8 : 0.3;
      ctx.shadowColor = 'rgba(226,232,240,0.8)';
      for (const s of state.stars) {
        const twinkle = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(t * s.freq + s.phase));
        ctx.shadowBlur = s.r > 1 ? 6 : 0;
        ctx.fillStyle = `rgba(226,232,240,${starBase * twinkle})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;

      // Overlapping ribbons glow when blended additively on the dark sky.
      ctx.globalCompositeOperation = dark ? 'screen' : 'source-over';
      for (const r of state.ribbons) {
        const colorHex = palette[(r.colorIdx + Math.floor(t * (colorMode === 'cycling' ? 0.4 : 0.04))) % palette.length];
        const { r: rc, g: gc, b: bc } = hexToRgb(colorHex);
        const shimmer = 0.75 + 0.25 * Math.sin(t * 0.4 + r.shimmerPhase);
        const peak = (dark ? 0.55 : 0.34) * shimmer;

        const grad = ctx.createLinearGradient(0, r.yBase - r.amplitude, 0, r.yBase + r.amplitude);
        grad.addColorStop(0, `rgba(${rc},${gc},${bc},0)`);
        grad.addColorStop(0.5, `rgba(${rc},${gc},${bc},${peak})`);
        grad.addColorStop(1, `rgba(${rc},${gc},${bc},0)`);

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(0, h);
        for (let x = 0; x <= w; x += 12) {
          const y = r.yBase
            + Math.sin(x * 0.005 + t * r.speedX + r.phase) * r.amplitude * 0.8
            + Math.cos(x * 0.012 + t * r.speedY + r.phase) * r.amplitude * 0.4;
          ctx.lineTo(x, y);
        }
        ctx.lineTo(w, h);
        ctx.closePath();
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
    },
  });
  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
